import React, { useState, useCallback, useId } from 'react';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import type { BookingContact, BookingPassenger, CancellationPolicy } from '../api/hotels.types';
import type { BookingFormData, QuoteRefreshNotice, QuotedPrice } from '../hooks/useHotelBooking';
import { parseCancellationDateFrom } from '../utils/cancellationPolicy';
import { formatHotelPrice } from '../utils/formatHotelPrice';
import type { BookingRoomLayout } from '../utils/buildBookingRooms';
import { useListingColors } from 'legacyfrontend/hooks/useListingColors';
import {
  LISTING_PRIMARY_BUTTON_CLASS,
  listingPrimaryButtonStyle,
} from 'legacyfrontend/hooks/listingPrimaryButton';

interface ContactErrors {
  name?: string;
  surname?: string;
  email?: string;
  phone?: string;
}

interface PassengerErrors {
  name?: string;
  surname?: string;
  birthdate?: string;
}

interface FormErrors {
  contact: ContactErrors;
  passengers: PassengerErrors[];
}

function adultPassenger(): BookingPassenger {
  return { type: 'AD', name: '', surname: '' };
}

function childPassenger(): BookingPassenger {
  return { type: 'CH', name: '', surname: '', birthdate: '' };
}

/** Adults first, then children, room after room — the order the provider quoted them. */
function buildInitialPassengers(rooms: BookingRoomLayout[]): BookingPassenger[] {
  return rooms.flatMap((room) => [
    ...Array.from({ length: Math.max(1, room.adults || 1) }, adultPassenger),
    ...Array.from({ length: Math.max(0, room.children || 0) }, childPassenger),
  ]);
}

/** Passengers typed before (draft), when they still fit the same rooms; otherwise empty ones. */
function initialPassengers(
  rooms: BookingRoomLayout[],
  draft?: BookingFormData | null,
): BookingPassenger[] {
  const fresh = buildInitialPassengers(rooms);
  const typed = draft?.rooms.flatMap((room) => room.passengers) ?? [];
  const fits =
    typed.length === fresh.length &&
    typed.every((passenger, i) => passenger.type === fresh[i].type);
  return fits ? typed.map(({ extraFields: _extraFields, ...passenger }) => passenger) : fresh;
}

function formatPolicyDate(value: string, locale: string): string {
  const date = parseCancellationDateFrom(value);
  return date ? date.toLocaleDateString(locale, { timeZone: 'UTC' }) : value;
}

/** Room id of each passenger built by `buildInitialPassengers`, by index. */
function buildPassengerRoomIds(rooms: BookingRoomLayout[]): number[] {
  return rooms.flatMap((room) =>
    Array.from(
      { length: Math.max(1, room.adults || 1) + Math.max(0, room.children || 0) },
      () => room.id,
    ),
  );
}

/** API requires strict E.164: "+" followed by 7 to 15 digits, no spaces or dashes. */
const E164_PHONE_REGEX = /^\+\d{7,15}$/;

function normalizePhone(phone: string): string {
  return phone.replace(/[\s-]/g, '');
}

/** Strips everything but digits and re-prepends "+" — the user only ever types digits. */
function formatPhoneInput(rawValue: string): string {
  return `+${rawValue.replace(/\D/g, '')}`;
}

function validateContact(contact: BookingContact, t: (k: string) => string): ContactErrors {
  const errors: ContactErrors = {};
  if (!contact.name.trim()) errors.name = t('hotels.booking.validation.nameRequired');
  if (!contact.surname.trim()) errors.surname = t('hotels.booking.validation.surnameRequired');
  if (!contact.email.trim()) {
    errors.email = t('hotels.booking.validation.emailRequired');
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)) {
    errors.email = t('hotels.booking.validation.emailInvalid');
  }
  if (normalizePhone(contact.phone) === '+') {
    errors.phone = t('hotels.booking.validation.phoneRequired');
  } else if (!E164_PHONE_REGEX.test(normalizePhone(contact.phone))) {
    errors.phone = t('hotels.booking.validation.phoneInvalid');
  }
  return errors;
}

function validatePassenger(p: BookingPassenger, t: (k: string) => string): PassengerErrors {
  const errors: PassengerErrors = {};
  if (!p.name.trim()) errors.name = t('hotels.booking.validation.nameRequired');
  if (!p.surname.trim()) errors.surname = t('hotels.booking.validation.surnameRequired');

  if (p.type === 'CH') {
    if (!p.birthdate?.trim()) {
      errors.birthdate = t('hotels.booking.validation.birthdateRequired');
    } else if (!/^\d{4}-\d{2}-\d{2}$/.test(p.birthdate)) {
      errors.birthdate = t('hotels.booking.validation.birthdateInvalid');
    }
  }
  return errors;
}

function hasErrors(errors: FormErrors): boolean {
  if (Object.keys(errors.contact).length > 0) return true;
  return errors.passengers.some((e) => Object.keys(e).length > 0);
}

interface FieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  hint?: string;
  placeholder?: string;
  type?: string;
  required?: boolean;
}

function Field({
  id,
  label,
  value,
  onChange,
  error,
  hint,
  placeholder,
  type = 'text',
  required,
}: FieldProps) {
  return (
    <div>
      <label
        className='mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500'
        htmlFor={id}
      >
        {label}
        {required ? (
          <span aria-hidden='true' className='ml-0.5 text-red-400'>
            *
          </span>
        ) : null}
      </label>
      <input
        className={`block w-full rounded-lg border px-3 py-2.5 text-sm text-gray-900 shadow-none transition-colors placeholder:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-0 ${
          error
            ? 'border-red-300 bg-red-50 focus:ring-red-400'
            : 'border-gray-200 bg-white hover:border-gray-300'
        }`}
        id={id}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        type={type}
        value={value}
      />
      {error ? (
        <p className='mt-1 flex items-center gap-1 text-xs text-red-500'>
          <span aria-hidden='true'>·</span>
          {error}
        </p>
      ) : hint ? (
        <p className='mt-1 text-xs text-gray-400'>{hint}</p>
      ) : null}
    </div>
  );
}

interface HotelBookingFormProps {
  hotelName?: string;
  rateName?: string;
  onSubmit: (data: BookingFormData) => void;
  onClose: () => void;
  isSubmitting: boolean;
  isNonRefundable?: boolean;
  /** Booked rooms and who sleeps in each — the passenger list is fixed to these. */
  rooms: BookingRoomLayout[];
  agencyCountryId?: number;
  /** Penalty schedule of the quote: shown before the user confirms. */
  cancellationPolicy?: CancellationPolicy[];
  /** Price confirmed by the availability quote, and the one shown in the results. */
  quotedPrice?: QuotedPrice | null;
  searchPrice?: number | null;
  currency?: string;
  /** Set when the form was reopened because a refreshed quote changed price or conditions. */
  quoteRefreshNotice?: QuoteRefreshNotice;
  /** Data typed before an error/refresh, to fill the form again. */
  initialData?: BookingFormData | null;
}

export function HotelBookingForm({
  hotelName,
  rateName,
  onSubmit,
  onClose,
  isSubmitting,
  isNonRefundable,
  rooms,
  agencyCountryId,
  cancellationPolicy = [],
  quotedPrice,
  searchPrice,
  currency = '',
  quoteRefreshNotice,
  initialData,
}: HotelBookingFormProps) {
  const { t, i18n } = useTranslation();
  const uid = useId();
  const { background: listingBackground, text: listingText } = useListingColors();

  const [contact, setContact] = useState<BookingContact>(() => ({
    name: initialData?.contact.name ?? '',
    surname: initialData?.contact.surname ?? '',
    email: initialData?.contact.email ?? '',
    phone: initialData?.contact.phone ?? '+',
  }));
  const [passengers, setPassengers] = useState<BookingPassenger[]>(() =>
    initialPassengers(rooms, initialData),
  );
  const [passengerRoomIds] = useState<number[]>(() => buildPassengerRoomIds(rooms));
  const [comments, setComments] = useState(initialData?.comments ?? '');
  const [errors, setErrors] = useState<FormErrors>({
    contact: {},
    passengers: passengers.map(() => ({})),
  });
  const [submitted, setSubmitted] = useState(false);

  const updateContact = useCallback((field: keyof BookingContact, value: string) => {
    setContact((prev) => ({ ...prev, [field]: value }));
  }, []);

  const updatePassenger = useCallback(
    (index: number, field: keyof BookingPassenger, value: string) => {
      setPassengers((prev) => prev.map((p, i) => (i === index ? { ...p, [field]: value } : p)));
    },
    [],
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      // The submit button is disabled while sending, but a fast double click can beat the re-render.
      if (isSubmitting) return;
      setSubmitted(true);

      const contactErrors = validateContact(contact, t);
      const passengerErrors = passengers.map((p) => validatePassenger(p, t));
      const next: FormErrors = { contact: contactErrors, passengers: passengerErrors };
      setErrors(next);

      if (hasErrors(next)) return;

      const submittedContact: BookingContact = {
        ...contact,
        phone: normalizePhone(contact.phone),
        countryId: agencyCountryId,
      };

      const bookedRooms = rooms.map((room) => ({
        id: room.id,
        passengers: passengers.filter((_, index) => passengerRoomIds[index] === room.id),
      }));

      onSubmit({
        contact: submittedContact,
        rooms: bookedRooms,
        comments: comments.trim() || undefined,
      });
    },
    [
      contact,
      passengers,
      passengerRoomIds,
      rooms,
      comments,
      t,
      onSubmit,
      agencyCountryId,
      isSubmitting,
    ],
  );

  const priceCurrency = quotedPrice?.currency || currency;
  const totalLabel = quotedPrice
    ? formatHotelPrice(quotedPrice.sale, priceCurrency, i18n.language)
    : searchPrice
      ? formatHotelPrice(searchPrice, currency, i18n.language)
      : undefined;
  const priceChanged =
    !!quotedPrice &&
    !!searchPrice &&
    Math.abs(quotedPrice.sale - searchPrice) > Math.max(0.05, searchPrice * 0.005);
  const penalties = [...cancellationPolicy]
    .filter((row) => parseCancellationDateFrom(row.dateFrom))
    .sort((a, b) => a.dateFrom.localeCompare(b.dateFrom));

  return (
    <div className='flex h-full flex-col overflow-hidden bg-white'>
      {/* Sticky header */}
      <div className='flex shrink-0 items-start justify-between border-b border-gray-100 bg-white px-5 py-4'>
        <div className='min-w-0 pr-3'>
          <h2 className='text-base font-semibold text-gray-900'>
            {isNonRefundable
              ? t('hotels.booking.inquiry.form.title')
              : t('hotels.booking.form.title')}
          </h2>
          {hotelName ? <p className='mt-0.5 truncate text-sm text-gray-500'>{hotelName}</p> : null}
          {rateName ? (
            <p className='mt-1 inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700'>
              {rateName}
            </p>
          ) : null}
          {isNonRefundable ? (
            <p className='mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700'>
              {t('hotels.booking.inquiry.form.notice')}
            </p>
          ) : null}
          {quoteRefreshNotice ? (
            <p
              className='mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700'
              role='alert'
            >
              {t(`hotels.booking.form.quoteRefreshed.${quoteRefreshNotice}`)}
            </p>
          ) : null}
          {totalLabel ? (
            <p className='mt-2 text-sm text-gray-700'>
              {`${t('hotels.booking.form.total')}: `}
              <span className='font-semibold'>{totalLabel}</span>
              {priceChanged && searchPrice ? (
                <span className='ml-1 text-xs text-amber-700'>
                  {t('hotels.booking.form.priceUpdated', {
                    previous: formatHotelPrice(searchPrice, currency, i18n.language),
                  })}
                </span>
              ) : null}
            </p>
          ) : null}
        </div>
        <button
          aria-label={t('hotels.booking.form.cancel')}
          className='shrink-0 rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600'
          onClick={onClose}
          type='button'
        >
          <CloseIcon fontSize='small' />
        </button>
      </div>

      {/* Scrollable body */}
      <div className='flex-1 overflow-y-auto'>
        <form className='flex flex-col gap-0' id={`${uid}-form`} noValidate onSubmit={handleSubmit}>
          {/* Contact section */}
          <section className='border-b border-gray-100 px-5 py-5'>
            <h3 className='mb-4 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-gray-400'>
              <PersonIcon fontSize='small' sx={{ fontSize: 14 }} />
              {t('hotels.booking.form.contact')}
            </h3>
            <div className='grid grid-cols-2 gap-3'>
              <Field
                error={submitted ? errors.contact.name : undefined}
                id={`${uid}-contact-name`}
                label={t('hotels.booking.form.name')}
                onChange={(v) => updateContact('name', v)}
                required
                value={contact.name}
              />
              <Field
                error={submitted ? errors.contact.surname : undefined}
                id={`${uid}-contact-surname`}
                label={t('hotels.booking.form.surname')}
                onChange={(v) => updateContact('surname', v)}
                required
                value={contact.surname}
              />
              <div className='col-span-2'>
                <Field
                  error={submitted ? errors.contact.email : undefined}
                  id={`${uid}-contact-email`}
                  label={t('hotels.booking.form.email')}
                  onChange={(v) => updateContact('email', v)}
                  required
                  type='email'
                  value={contact.email}
                />
              </div>
              <div className='col-span-2'>
                <Field
                  error={submitted ? errors.contact.phone : undefined}
                  hint={t('hotels.booking.form.phoneHint')}
                  id={`${uid}-contact-phone`}
                  label={t('hotels.booking.form.phone')}
                  onChange={(v) => updateContact('phone', formatPhoneInput(v))}
                  placeholder='+5491155552020'
                  required
                  type='tel'
                  value={contact.phone}
                />
              </div>
            </div>
          </section>

          {/* Passengers section */}
          <section className='border-b border-gray-100 px-5 py-5'>
            <div className='mb-4 flex items-center justify-between'>
              <h3 className='flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-gray-400'>
                <ChildCareIcon fontSize='small' sx={{ fontSize: 14 }} />
                {t('hotels.booking.form.passengers')}
              </h3>
            </div>

            <div className='space-y-5'>
              {rooms.map((room) => {
                const roomPassengers = passengers
                  .map((passenger, index) => ({ passenger, index }))
                  .filter(({ index }) => passengerRoomIds[index] === room.id);

                return (
                  <div className='space-y-3' key={`room-${room.id}`}>
                    {rooms.length > 1 ? (
                      <h4 className='text-sm font-semibold text-gray-700'>
                        {t('hotels.booking.form.room', { number: room.id })}
                      </h4>
                    ) : null}
                    {roomPassengers.map(({ passenger, index }, position) => {
                      const pErrors = submitted ? (errors.passengers[index] ?? {}) : {};
                      const adultCount = roomPassengers
                        .slice(0, position + 1)
                        .filter(({ passenger: p }) => p.type === 'AD').length;
                      const childCount = roomPassengers
                        .slice(0, position + 1)
                        .filter(({ passenger: p }) => p.type === 'CH').length;
                      const typeLabel =
                        passenger.type === 'AD'
                          ? `${t('hotels.booking.form.adult')} ${adultCount}`
                          : `${t('hotels.booking.form.child')} ${childCount}`;

                      return (
                        <div
                          className='rounded-xl border border-gray-100 bg-gray-50/70 p-4'
                          key={`pax-${index}`}
                        >
                          <div className='mb-3 flex items-center justify-between'>
                            <div className='flex items-center gap-1.5'>
                              <span
                                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
                                  passenger.type === 'AD'
                                    ? 'bg-blue-100 text-blue-700'
                                    : 'bg-purple-100 text-purple-700'
                                }`}
                              >
                                {typeLabel}
                              </span>
                            </div>
                          </div>
                          <div className='grid grid-cols-2 gap-3'>
                            <Field
                              error={pErrors.name}
                              id={`${uid}-p${index}-name`}
                              label={t('hotels.booking.form.name')}
                              onChange={(v) => updatePassenger(index, 'name', v)}
                              required
                              value={passenger.name}
                            />
                            <Field
                              error={pErrors.surname}
                              id={`${uid}-p${index}-surname`}
                              label={t('hotels.booking.form.surname')}
                              onChange={(v) => updatePassenger(index, 'surname', v)}
                              required
                              value={passenger.surname}
                            />
                            {passenger.type === 'CH' ? (
                              <div className='col-span-2'>
                                <Field
                                  error={pErrors.birthdate}
                                  id={`${uid}-p${index}-birthdate`}
                                  label={t('hotels.booking.form.birthdate')}
                                  onChange={(v) => updatePassenger(index, 'birthdate', v)}
                                  placeholder='YYYY-MM-DD'
                                  required
                                  value={passenger.birthdate ?? ''}
                                />
                              </div>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </section>

          {/* Cancellation conditions: shown before confirming */}
          <section className='border-b border-gray-100 px-5 py-5'>
            <h3 className='mb-2 text-xs font-bold uppercase tracking-widest text-gray-400'>
              {t('hotels.booking.form.cancellationTitle')}
            </h3>
            {penalties.length > 0 ? (
              <ul className='space-y-1 text-sm text-gray-700'>
                {!isNonRefundable ? (
                  <li className='text-emerald-700'>
                    {t('hotels.booking.form.freeCancellationUntil', {
                      date: formatPolicyDate(penalties[0].dateFrom, i18n.language),
                    })}
                  </li>
                ) : null}
                {penalties.map((row) => (
                  <li key={`${row.dateFrom}-${row.amount}`}>
                    {t('hotels.booking.form.penaltyFrom', {
                      date: formatPolicyDate(row.dateFrom, i18n.language),
                      amount: formatHotelPrice(row.amount, row.currency, i18n.language) ?? '-',
                    })}
                  </li>
                ))}
              </ul>
            ) : (
              <p className='text-sm text-amber-700'>
                {t('hotels.booking.form.noCancellationPolicy')}
              </p>
            )}
          </section>

          {/* Comments section */}
          <section className='px-5 py-5'>
            <label
              className='mb-1 block text-xs font-bold uppercase tracking-widest text-gray-400'
              htmlFor={`${uid}-comments`}
            >
              {t('hotels.booking.form.comments')}
            </label>
            <textarea
              className='block w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-300 transition-colors hover:border-gray-300 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-0'
              id={`${uid}-comments`}
              onChange={(e) => setComments(e.target.value)}
              placeholder={t('hotels.booking.form.commentsPlaceholder')}
              rows={2}
              value={comments}
            />
          </section>
        </form>
      </div>

      {/* Sticky footer */}
      <div className='shrink-0 border-t border-gray-100 bg-white px-5 py-4'>
        <div className='flex gap-3'>
          <button
            className='flex-1 rounded-xl border border-gray-200 bg-white py-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50'
            disabled={isSubmitting}
            onClick={onClose}
            type='button'
          >
            {t('hotels.booking.form.cancel')}
          </button>
          <button
            className={`flex-[2] rounded-xl py-3 text-sm ${LISTING_PRIMARY_BUTTON_CLASS}`}
            disabled={isSubmitting}
            form={`${uid}-form`}
            style={listingPrimaryButtonStyle(listingBackground, listingText)}
            type='submit'
          >
            {isSubmitting
              ? t('hotels.booking.form.submitting')
              : isNonRefundable
                ? t('hotels.booking.inquiry.form.submit')
                : t('hotels.booking.form.submit')}
          </button>
        </div>
      </div>
    </div>
  );
}
